import { z } from 'zod'

/**
 * 디지털 화환 (BIZ-126 — 목업의 실구현. 옛 이름 '디지털 근조기' — 근조기는 별도 판매 상품으로 분리).
 *
 * 회사(개인 주체 포함)가 디지털 화환 디자인을 골라두면, 제휴 장례식장으로 화환을 보낼 때
 * 주문에 애드온으로 실리고(orders.digital_wreath_design) 부고장·장례식장 화면에 함께
 * 올라간다(실연동은 BIZ-125). 문구·보낸 분은 주문의 리본 필드를 그대로 쓴다.
 *
 * 디자인 카탈로그(색·이름)는 web 코드(WREATH_DESIGNS)가 정본 — 서버는 id만 저장·검증한다.
 * 새 디자인은 여기 enum과 web 카탈로그에 같이 추가한다.
 */
export const wreathDesignId = z.enum(['gold', 'slate', 'pine', 'night'])
export type WreathDesignId = z.infer<typeof wreathDesignId>

/** 내 디지털 화환 설정. 아직 안 골랐으면 null — 화면은 기본 디자인으로 그린다. */
export const digitalWreathSettingResponse = z.object({
  designId: wreathDesignId.nullable(),
})
export type DigitalWreathSettingResponse = z.infer<typeof digitalWreathSettingResponse>

export const updateDigitalWreathRequest = z.object({
  designId: wreathDesignId,
})
export type UpdateDigitalWreathRequest = z.infer<typeof updateDigitalWreathRequest>

// 제휴 장례식장 판정은 장례식장 엔티티(funeralHall.ts, BIZ-90)의 isPartner로 옮겨갔다.

// ── 송출(BIZ-125) ─────────────────────────────────────────────
//
// 디지털 화환을 부고장·장례식장 디스플레이(외부 시스템 A)로 내보내는 송출 상태. 주문당 1건.
// op = 지금 할 일(PUBLISH 게시·갱신 / RETRACT 내리기), status = 그 일의 진행.
// 기업 뷰엔 FAILED를 PENDING으로 접어 보낸다(재시도·재송출은 운영자 몫 — 고객 불안 방지).

export const digitalWreathDispatchOp = z.enum(['PUBLISH', 'RETRACT'])
export type DigitalWreathDispatchOp = z.infer<typeof digitalWreathDispatchOp>

export const digitalWreathDispatchStatus = z.enum(['PENDING', 'SENT', 'FAILED', 'CANCELLED'])
export type DigitalWreathDispatchStatus = z.infer<typeof digitalWreathDispatchStatus>

export const digitalWreathDispatchView = z.object({
  op: digitalWreathDispatchOp,
  status: digitalWreathDispatchStatus,
  /** 마지막으로 게시에 성공한 시각. 한 번도 안 올라갔으면 null. */
  publishedAt: z.string().nullable(),
  updatedAt: z.string(),
  /** 운영자 뷰에서만 채운다. */
  attemptCount: z.number().int().optional(),
  failedReason: z.string().nullable().optional(),
})
export type DigitalWreathDispatchView = z.infer<typeof digitalWreathDispatchView>

/** 운영자 재송출 응답 — FAILED를 즉시 재시도 대기로 되돌린 뒤의 상태. */
export const digitalWreathResendResponse = z.object({
  dispatch: digitalWreathDispatchView,
})
export type DigitalWreathResendResponse = z.infer<typeof digitalWreathResendResponse>

export const DigitalWreathErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  DISPATCH_NOT_FOUND: 'DISPATCH_NOT_FOUND', // 송출 이력이 없는 주문
  DISPATCH_NOT_RETRIABLE: 'DISPATCH_NOT_RETRIABLE', // 실패 확정(FAILED)이 아니면 재송출 불가
} as const
